/* eslint-disable no-unused-vars */
import { createTextGl, zoomToFitBox2D } from '@/utils/forgeUtils';
import { TagHandlerBase } from './tagHandlerBase';
import store from '@/store';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
const THREE = window.THREE;

/**
 * Handler for inspection tags
 */
export class InspectionHandler extends TagHandlerBase {
    /**
     * Check if tag type should be processed
     * @param {string} type - Tag type
     * @param {Object} displaySettings - Display settings
     * @returns {boolean} Should process inspection
     */
    shouldProcess(type, displaySettings) {
        return displaySettings.showInspections && 
               ((type === 'inspection' && !store.state.forgeSettings.isAMIS) || 
                (type === 'amisTag' && store.state.forgeSettings.isAMIS) || 
                (type === 'severity' && store.getters.IS_AI_INSPECTION_PROPERTY_SELECTED));
    }
    
    /**
     * Collect inspection data
     * @param {Object} settings - Display settings
     * @param {Object} viewerData - Viewer position data
     * @returns {Object} Collected inspection data
     */
    collectData(settings, viewerData) {
        const { isGlobalSearchFilter, isGlobalSearchTabShown, displaySettings } = settings;
        const data = {};
        
        // Clear any existing cluster text meshes
        this.clearClusterTextMeshes();
        
        // Get inspection type from extension
        const type = this.extension.currentTagType || 'inspection';
        
        // Skip if not applicable
        if (!this.shouldProcess(type, displaySettings)) return data;
        
        // Get AMIS tags if needed
        let amisTags = this.getAMISTags(isGlobalSearchTabShown, isGlobalSearchFilter);

        if (isGlobalSearchFilter) {
            let isolatedTags = store.getters['GET_ISOLATED_IDS'];    
            if (isolatedTags.size > 0) {
                amisTags = amisTags.filter(t => isolatedTags.has(t.id));
            }
        }
        
        // Handle different inspection types
        if (type === 'inspection' && !store.state.forgeSettings.isAMIS) {
            this.collectRegularInspectionData(data, amisTags, isGlobalSearchTabShown, isGlobalSearchFilter, viewerData);
        } else if (type === 'amisTag' && store.state.forgeSettings.isAMIS) {
            this.collectAMISTagData(data, amisTags, isGlobalSearchFilter, viewerData);
        } else if (type === 'severity' && store.getters.IS_AI_INSPECTION_PROPERTY_SELECTED) {
            this.collectSeverityInspectionData(data, amisTags, isGlobalSearchTabShown, isGlobalSearchFilter, viewerData);
        }
        
        return data;
    }
    
    /**
     * Get AMIS tags based on filters
     * @param {boolean} isGlobalSearchTabShown - Global search tab shown flag
     * @param {boolean} isGlobalSearchFilter - Global search filter flag
     * @returns {Array} AMIS tags
     */
    getAMISTags(isGlobalSearchTabShown, isGlobalSearchFilter) {
        let amisTags = [];
        // Comment out global search filtering for now
        /*
        if (isGlobalSearchTabShown && isGlobalSearchFilter) {
            const InspectionSelectedFilter = store.state.TAGS.InspectionSelectedFilter ?? 'Remaining Thickness';
            const selectedColorsOptions = store.state.TAGS.InspectionAvailableColorOptions;
            
            store.state.forgeService.advancedUpdateAMISTagsColors(
                store.state.filteredInspections, 
                InspectionSelectedFilter, 
                selectedColorsOptions, 
                '#0f7968'
            );
            amisTags = store.state.filteredInspections.filter(t => !!t.point);
        } else 
        */
        if (store.state.TAGS.InspectionSelectedFilter) {
            if (store.state.TAGS.InspectionSelectedFilter != 'Inspection Tag') {
                amisTags = store.getters['GET_ALL_AMIS_FILTERED_INSPECTIONS'].filter(t => !!t.point);
            }
        }
        return amisTags;
    }
    
    /**
     * Collect regular inspection data
     * @param {Object} data - Data collection object
     * @param {Array} amisTags - AMIS tags
     * @param {boolean} isGlobalSearchTabShown - Global search tab shown flag
     * @param {boolean} isGlobalSearchFilter - Global search filter flag
     * @param {Object} viewerData - Viewer position data
     */
    collectRegularInspectionData(data, amisTags, isGlobalSearchTabShown, isGlobalSearchFilter, viewerData) {
        let inspections = this.isViewer2d() 
            ? store.state.forgeService.getInspectionsInLevel(store.state.selectedLevel, amisTags) 
            : amisTags;
            
        if (this.isViewer2d()) {
            inspections = store.state.forgeService.updateListWithEditingTag(inspections);
            
            // Filter inspections before clustering
            inspections = inspections.filter(inspection => {
                // Skip if inspection is for a different level
                if (inspection.level2D && (!this.isViewer2d() || inspection.level2D != store.state.selectedLevel.name)) return false;
                
                // Comment out global search filtering for now
                /*
                // Skip if filtered out by global search
                if (isGlobalSearchTabShown && isGlobalSearchFilter && !store.state.filteredInspections.includes(inspection)) return false;
                */
                
                return true;
            });
            
            // Group inspections by pin color before clustering
            const inspectionsByType = {};
            
            inspections.forEach(inspection => {
                const pinColor = inspection.pinColor || 'default';
                if (!inspectionsByType[pinColor]) {
                    inspectionsByType[pinColor] = [];
                }
                inspectionsByType[pinColor].push(inspection);
            });
            
            // Apply clustering to each type group separately
            for (const [pinColor, typeInspections] of Object.entries(inspectionsByType)) {
                if (typeInspections.length > 0) {
                    const { items, clusters } = this.getTagsClustersAndItems2D(typeInspections, viewerData.position);
                   
                    // Process clusters for this type
                    this.processInspectionTypeClustersByColor(clusters, data, pinColor);
                    
                    // Add individual items to the data collection
                    items.forEach(inspection => {
                        // Get point in viewer space
                        let point = this.getPointForInspection(inspection);
                        if (!point) return;
                        
                        // Create point cloud data
                        const pcData = {
                            name: inspection.name,
                            thumbnailURL: inspection.thumbnailUrl,
                            object: inspection,
                            pinColor: inspection.getPinVector(),
                            point: point,
                            onClick: (event) => {
                                store.dispatch('FETCH_INSPECTION_TAG_BY_ID', inspection.id).then(tag => {
                                    store.state.forgeService.onClickOn3dIcon(tag, event, this.extension.viewType);
                                });
                            }
                        };
                        
                        // Add to collection by pin color
                        data[inspection.pinColor] ? data[inspection.pinColor].push(pcData) : data[inspection.pinColor] = [pcData];
                        
                        // Draw tag area
                        this.drawTagArea(inspection);
                    });
                }
            }
        } else {
            // Apply filtering for 3D view as well
            inspections = inspections.filter(inspection => {
                // Comment out global search filtering for now
                /*
                // Skip if filtered out by global search
                if (isGlobalSearchTabShown && isGlobalSearchFilter && !store.state.filteredInspections.includes(inspection)) return false;
                */
                
                return true;
            });
            
            inspections.forEach(inspection => {
                // Get point in viewer space (already filtered for level and search)
                let point = this.getPointForInspection(inspection);
                if (!point) return;
                
                // Create point cloud data
                const pcData = {
                    name: inspection.name,
                    thumbnailURL: inspection.thumbnailUrl,
                    object: inspection,
                    pinColor: inspection.getPinVector(),
                    point: point,
                    onClick: (event) => {
                        store.dispatch('FETCH_INSPECTION_TAG_BY_ID', inspection.id).then(tag => {
                            store.state.forgeService.onClickOn3dIcon(tag, event, this.extension.viewType);
                        });
                    }
                };
                
                // Add to collection by pin color
                data[inspection.pinColor] ? data[inspection.pinColor].push(pcData) : data[inspection.pinColor] = [pcData];
                
                // Draw tag area
                this.drawTagArea(inspection);
            });
        }
    }
    
    /**
     * Process clusters for a specific inspection type by color
     * @param {Array} clusters - Inspection clusters
     * @param {Object} data - Data collection object
     * @param {string} pinColor - Pin color representing the type
     */
    processInspectionTypeClustersByColor(clusters, data, pinColor) {
        // Get font color based on pin color
        const fontColor = store.getters.GET_SELECTED_COLORS_OPTIONS?.length ? this.getFontColor(pinColor) : 0xffffff;

        clusters.forEach(cluster => {
            // Calculate average position for cluster
            const avgPoint = this.calculateAveragePoint(cluster);
            // Calculate the center point of the cluster
            let points = cluster.map(o => store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(o.point));
            
            // Create cluster data with type-specific key
            const clusterKey = `${pinColor}-cluster`;
            const clusterData = {
                name: cluster ? cluster.length : cluster.length,
                point: avgPoint,
                pinColor: pinColor,
                points: points,
                // Create a custom function for cluster text creation
                clusterCreate: () => {
                    // Draw text for cluster count if needed
                    let size = 12 / (this.extension.viewer.worldToClient(new THREE.Vector2(1, 0)).x - 
                                    this.extension.viewer.worldToClient(new THREE.Vector2(0, 0)).x);
                    let textPt = new THREE.Vector3(avgPoint.x, avgPoint.y, avgPoint.z + .05);
                    
                    const textMesh = this.createTextMesh(
                        cluster ? cluster.length.toString() : cluster.length.toString(), 
                        size, 
                        fontColor, 
                        textPt
                    );
                    this.clusterTextMeshes.push(textMesh);
                },
                onClick: () => {
                    // Handle click on cluster (zoom to bounds)
                    this.onClusterClick(cluster);
                }
            };
            
            // Add to collection with type-specific key
            data[clusterKey] ? data[clusterKey].push(clusterData) : data[clusterKey] = [clusterData];
        });
    }
    
    /**
     * Calculate average point from an array of items
     * @param {Array} items - Array of items with point property
     * @returns {Object} Average point (THREE.Vector3)
     */
    calculateAveragePoint(items) {
        if (!items || items.length === 0) {
            return new THREE.Vector3(0, 0, 0);
        }
        
        const sum = new THREE.Vector3(0, 0, 0);
        let count = 0;
        
        items.forEach(item => {
            if (item.point) {
                let point  = this.getPointForInspection(item);
                sum.add(new THREE.Vector3(point.x, point.y, point.z));
                count++;
            }
        });
        
        if (count === 0) {
            return new THREE.Vector3(0, 0, 0);
        }
        
        return new THREE.Vector3(
            sum.x / count,
            sum.y / count,
            sum.z / count
        );
    }
    
    /**
     * Process inspection clusters
     * @param {Array} clusters - Inspection clusters
     * @param {Object} data - Data collection object
     */
    processInspectionClusters(clusters, data) {
        // Use type-specific cluster processing
        this.processInspectionTypeClustersByColor(clusters, data, 'inspection');
    }
    
    /**
     * Process individual inspection
     * @param {Object} inspection - Inspection object
     * @param {Object} data - Data collection object
     * @param {boolean} isGlobalSearchTabShown - Global search tab shown flag
     * @param {boolean} isGlobalSearchFilter - Global search filter flag
     */
    processInspection(inspection, data, isGlobalSearchTabShown, isGlobalSearchFilter) {
        const _this = this;
        
        // Skip if inspection is for a different level
        if (inspection.level2D && (!this.isViewer2d() || inspection.level2D != store.state.selectedLevel.name)) return;
        
        // Skip if filtered out by global search
        if (isGlobalSearchTabShown && isGlobalSearchFilter && !store.state.filteredInspections.includes(inspection)) return;
        
        // Get point in viewer space
        let point = this.getPointForInspection(inspection);
        if (!point) return;
        
        // Create point cloud data
        const pcData = {
            name: inspection.name,
            thumbnailURL: inspection.thumbnailUrl,
            object: inspection,
            pinColor: inspection.getPinVector(),
            point: point,
            onClick: (event) => {
                store.dispatch('FETCH_INSPECTION_TAG_BY_ID', inspection.id).then(tag => {
                    store.state.forgeService.onClickOn3dIcon(tag, event, _this.extension.viewType);
                });
            }
        };
        
        // Add to collection by pin color
        data[inspection.pinColor] ? data[inspection.pinColor].push(pcData) : data[inspection.pinColor] = [pcData];
        
        // Draw tag area
        this.drawTagArea(inspection);
    }
    
    /**
     * Collect AMIS tag data
     * @param {Object} data - Data collection object
     * @param {Array} amisTags - AMIS tags
     * @param {boolean} isGlobalSearchFilter - Global search filter flag
     * @param {Object} viewerData - Viewer position data
     */
    collectAMISTagData(data, amisTags, isGlobalSearchFilter, viewerData) {
        const _this = this;
        
        let inspections = this.isViewer2d() 
            ? store.state.forgeService.getInspectionsInLevel(store.state.selectedLevel, amisTags) 
            : amisTags;
        
        // Comment out global search filtering for now
        /*
        // Filter inspections before clustering
        if (isGlobalSearchFilter) {
            inspections = inspections.filter(tag => store.state.filteredInspections.includes(tag));
        }
        */
        
        // Group tags by pin color before clustering
        if (this.isViewer2d() && inspections.length > 0) {
            const inspectionsByType = {};
            
            inspections.forEach(inspection => {
                const pinColor = inspection.pinColor || 'default';
                if (!inspectionsByType[pinColor]) {
                    inspectionsByType[pinColor] = [];
                }
                inspectionsByType[pinColor].push(inspection);
            });
            
            // Apply clustering to each type group separately
            for (const [pinColor, typeInspections] of Object.entries(inspectionsByType)) {
                if (typeInspections.length > 0) {
                    const { items, clusters } = this.getTagsClustersAndItems2D(typeInspections, viewerData.position);
                    
                    // Process clusters for this type
                    this.processInspectionTypeClustersByColor(clusters, data, pinColor);
                    
                    // Add individual items to the data collection
                    items.forEach(tag => {
                        // Get point in viewer space
                        let point = this.getPointForAMISTag(tag);
                        if (!point) return;
                        
                        // Create point cloud data
                        const pcData = {
                            name: tag.name,
                            thumbnailURL: tag.thumbnailUrl,
                            object: tag,
                            pinColor: pinColor,
                            point: point,
                            onClick: (event) => {
                                store.dispatch('FETCH_INSPECTION_TAG_BY_ID', tag.id).then(tag => {
                                    store.state.forgeService.onClickOn3dIcon(tag, event, _this.extension.viewType);
                                });
                            }
                        };
                        
                        // Add to collection by pin color
                        data[pinColor] ? data[pinColor].push(pcData) : data[pinColor] = [pcData];
                    });
                }
            }
        } else {
            inspections.forEach(tag => {
                // Get point in viewer space
                let point = this.getPointForAMISTag(tag);
                if (!point) return;
                
                // Create point cloud data
                const pcData = {
                    name: tag.name,
                    thumbnailURL: tag.thumbnailUrl,
                    object: tag,
                    pinColor: tag.pinColor || 'default',
                    point: point,
                    onClick: (event) => {
                        store.dispatch('FETCH_INSPECTION_TAG_BY_ID', tag.id).then(tag => {
                            store.state.forgeService.onClickOn3dIcon(tag, event, _this.extension.viewType);
                        });
                    }
                };
                
                // Add to collection by pin color
                data[tag.pinColor] ? data[tag.pinColor].push(pcData) : data[tag.pinColor] = [pcData];
            });
        }
    }
    
    /**
     * Collect severity inspection data
     * @param {Object} data - Data collection object
     * @param {Array} amisTags - AMIS tags
     * @param {boolean} isGlobalSearchTabShown - Global search tab shown flag
     * @param {boolean} isGlobalSearchFilter - Global search filter flag
     * @param {Object} viewerData - Viewer position data
     */
    collectSeverityInspectionData(data, amisTags, isGlobalSearchTabShown, isGlobalSearchFilter, viewerData) {
        const _this = this;
        
        let inspections = this.isViewer2d() 
            ? store.state.forgeService.getInspectionsInLevel(store.state.selectedLevel, amisTags) 
            : amisTags;
            
        if (this.isViewer2d()) {
            inspections = store.state.forgeService.updateListWithEditingTag(inspections);
            
            // Filter inspections before clustering
            inspections = inspections.filter(inspection => {
                // Skip if no severity data
                if (!inspection.severity) return false;
                
                // Skip if inspection is for a different level
                if (inspection.level2D && (!this.isViewer2d() || inspection.level2D != store.state.selectedLevel.name)) return false;
                
                // Comment out global search filtering for now
                /*
                // Skip if filtered out by global search
                if (isGlobalSearchTabShown && isGlobalSearchFilter && !store.state.filteredInspections.includes(inspection)) return false;
                */
                
                return true;
            });
            
            // Group inspections by severity before clustering
            const inspectionsBySeverity = {};
            
            inspections.forEach(inspection => {
                const severity = inspection.severity || 'default';
                if (!inspectionsBySeverity[severity]) {
                    inspectionsBySeverity[severity] = [];
                }
                inspectionsBySeverity[severity].push(inspection);
            });
            
            // Apply clustering to each severity group separately
            for (const [severity, severityInspections] of Object.entries(inspectionsBySeverity)) {
                if (severityInspections.length > 0) {
                    const { items, clusters } = this.getTagsClustersAndItems2D(severityInspections, viewerData.position);
                    
                    // Process clusters for this severity
                    this.processInspectionTypeClustersByColor(clusters, data, severity);
                    
                    // Add individual items to the data collection
                    items.forEach(inspection => {
                        // Get point in viewer space
                        let point = this.getPointForInspection(inspection);
                        if (!point) return;
                        
                        // Create point cloud data
                        const pcData = {
                            name: inspection.name,
                            thumbnailURL: inspection.thumbnailUrl,
                            object: inspection,
                            pinColor: inspection.getPinVector(),
                            point: point,
                            onClick: (event) => {
                                store.dispatch('FETCH_INSPECTION_TAG_BY_ID', inspection.id).then(tag => {
                                    store.state.forgeService.onClickOn3dIcon(tag, event, _this.extension.viewType);
                                });
                            }
                        };
                        
                        // Add to collection by severity
                        data[severity] ? data[severity].push(pcData) : data[severity] = [pcData];
                        
                        // Draw tag area
                        this.drawTagArea(inspection);
                    });
                }
            }
        } else {
            // Filter inspections for 3D view before processing
            inspections = inspections.filter(inspection => {
                // Skip if no severity data
                if (!inspection.severity) return false;
                
                // Comment out global search filtering for now
                /*
                // Skip if filtered out by global search
                if (isGlobalSearchTabShown && isGlobalSearchFilter && !store.state.filteredInspections.includes(inspection)) return false;
                */
                
                return true;
            });
            
            inspections.forEach(inspection => {
                // Get point in viewer space (already filtered)
                let point = this.getPointForInspection(inspection);
                if (!point) return;
                
                // Create point cloud data
                const pcData = {
                    name: inspection.name,
                    thumbnailURL: inspection.thumbnailUrl,
                    object: inspection,
                    pinColor: inspection.getPinVector(),
                    point: point,
                    onClick: (event) => {
                        store.dispatch('FETCH_INSPECTION_TAG_BY_ID', inspection.id).then(tag => {
                            store.state.forgeService.onClickOn3dIcon(tag, event, _this.extension.viewType);
                        });
                    }
                };
                
                // Add to collection by severity
                const severity = inspection.severity;
                data[severity] ? data[severity].push(pcData) : data[severity] = [pcData];
                
                // Draw tag area
                this.drawTagArea(inspection);
            });
        }
    }
    
    /**
     * Get point in viewer space for inspection
     * @param {Object} inspection - Inspection object
     * @returns {Object|null} Point in viewer space
     */
    getPointForInspection(inspection) {
        try {
            if (!inspection.point) {
                debugWarn('inspectionHandler', `Inspection ${inspection.id} has no point`);
            }

        } catch (error) {
            debugError('inspectionHandler', 'Error getting point for inspection', { inspection, error });
        }

        let point = this.isViewer2d() 
            ? store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(inspection.point) 
            : inspection.getPointInView();
        
        // Skip if outside crop box in 3D view
        if (!this.isViewer2d() && !store.state.forgeService.getMainExtension()?.isWithinCropBox(point)) {
            return null;
        }
        
        return point;
    }
    
    /**
     * Get point in viewer space for AMIS tag
     * @param {Object} tag - AMIS tag
     * @returns {Object|null} Point in viewer space
     */
    getPointForAMISTag(tag) {
        let point = this.isViewer2d() 
            ? store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(tag.point) 
            : store.state.forgeService.worldToView3d(tag.point, tag.point);
        
        // Skip if outside crop box in 3D view
        if (!this.isViewer2d() && !store.state.forgeService.getMainExtension()?.isWithinCropBox(point)) {
            return null;
        }
        
        return point;
    }

    /**
     * Handle click on a cluster
     * @param {Object} cluster - Cluster object
     */
    onClusterClick(cluster) {
        if (!this.isViewer2d()) return;

        // Create a bounding box containing all items in the cluster
        const items = cluster;
        const points = items.map(item => item.point).filter(point => !!point).map(point => {
            return store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(point)
        });

        if (points.length > 0) {
            let boundingBox = new THREE.Box3().setFromPoints(points);
            zoomToFitBox2D(this.extension.viewer, boundingBox);
        }
    }

    /**
     * Create text mesh for clusters
     * @param {string} text - Text to display
     * @param {number} size - Text size
     * @param {number} color - Text color
     * @param {Object} position - Text position
     * @returns {Object} Text mesh
     */
    createTextMesh(text, size, color, position) {
        // Create text for the cluster count
        const textMesh = createTextGl(text, size, color, position, this.extension.viewer, this.extension.sceneName);
        return textMesh;
    }

    /**
     * Get font color based on pin color
     * @param {string} pinColor - Pin color
     * @returns {number} Font color
     */
    getFontColor(pinColor) {
        // Use white font color for dark pin colors using luminance calculation
        const luminance = this.calculateLuminance(pinColor);
        return luminance < 0.5 ? 0xffffff : 0x000000;
    }

    /**
     * Calculate luminance for a color
     * @param {string} color - Color value
     * @returns {number} Luminance value
     */
    calculateLuminance(color) {
        const hex = color.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    }
}
